import { PRODUCT_IDS } from '@adsk/alloy-provider';

declare global {
  interface Window {
    SUPPORT_SCRIPTS_LOADED: boolean;
    embedded_svc: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

export type ProductSupportIds = {
  id: string;
  buttonId: string;
  eswLiveAgentDevName: string;
};

export type ProductId = keyof typeof PRODUCTS | string;

const PRODUCTS: {
  [key: string]: Record<'id' | 'buttonId' | 'eswLiveAgentDevName', string>;
} = {
  [PRODUCT_IDS.PROJECT_ADMINISTRATION]: {
    id: 'Admin_Chat',
    buttonId: '5736T000000sY1g',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAlUAO_17b5b66e223',
  },
  [PRODUCT_IDS.BUILD]: {
    id: 'Build_Chat',
    buttonId: '5736T000000sY1M',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZARUA4_17b5b493e4d',
  },
  [PRODUCT_IDS.DOCS]: {
    id: 'Docs_Chat',
    buttonId: '5736T000000sY1l',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAqUAO_17b5b6970ef',
  },
  [PRODUCT_IDS.DESIGN_COLLABORATION]: {
    id: 'Collaborate_Design_Chat',
    buttonId: '5736T000000sY1b',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAgUAO_17b5b63684a',
  },
  [PRODUCT_IDS.INSIGHT]: {
    id: 'Insight_Chat',
    buttonId: '5736T000000sY1R',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAcUAO_17b5b6c5956',
  },
  [PRODUCT_IDS.MODEL_COORDINATION]: {
    id: 'Collaborate_Model_Chat',
    buttonId: '5736T000000sY1W',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAbUAO_17b5b5f7377',
  },
  [PRODUCT_IDS.TAKEOFF]: {
    id: 'Takeoff_Chat',
    buttonId: '5736T000000sY1q',
    eswLiveAgentDevName:
      'EmbeddedServiceLiveAgent_Parent04I6T000000XZAWUA4_17b5b57760d',
  },
};

const CSS_STYLES = `
  .embeddedServiceHelpButton .helpButton .uiButton {
    background-color: #006eaf !important;
    font-family: "Artifakt Element", sans-serif !important;
    animation: delayShowServiceHelpButton 2s 1;
  }

  @keyframes delayShowServiceHelpButton {
    0% {
      opacity: 0;
    }
    66% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .embeddedServiceHelpButton .helpButton,
  .embeddedServiceSidebar.sidebarMaximized.layout-docked .dockableContainer {
    left: 10px;
    right: auto;
    max-height: 570px;
  }
  .embeddedServiceHelpButton .helpButton {
    width: 147px;
  }
  .embeddedServiceHelpButton .uiButton .helpButtonLabel {
    justify-content: initial !important;
    margin-left: 4px;
  }
  .embeddedServiceSidebar .wrapper.chatMessage {
    min-height: auto !important;
  }
  .embeddedServiceSidebar .uiButton--default {
    background: #0085DE;
    border: none;
    box-shadow: none;
    padding: 8px 15px;
    text-shadow: none;
  }
  .embeddedServiceSidebar .uiButton--default span {
    color: #FFF;
  }
  .offlineSupportUI .embeddedServiceSidebarForm .fieldList {
    padding: 0;
  }
`;

function initializeChat(product: ProductId = PRODUCT_IDS.BUILD) {
  if (!URLSearchParams) return;

  const PRODUCT =
    PRODUCTS[product] || PRODUCTS[PRODUCT_IDS.PROJECT_ADMINISTRATION];

  const initESW = function (gslbBaseURL: string | null) {
    window.embedded_svc.settings.displayHelpButton = true;
    window.embedded_svc.settings.language = ''; // e.g. 'en' or 'en-US'
    window.embedded_svc.settings.enabledFeatures = ['LiveAgent'];
    window.embedded_svc.settings.entryFeature = 'LiveAgent';
    window.embedded_svc.init(
      'https://plangrid.my.salesforce.com',
      'https://adsk-acs.force.com',
      gslbBaseURL,
      '00Dd0000000hAll',
      PRODUCT.id,
      {
        baseLiveAgentContentURL:
          'https://c.la4-c2-ia5.salesforceliveagent.com/content',
        deploymentId: '5720W000001YVMK',
        buttonId: PRODUCT.buttonId,
        baseLiveAgentURL: 'https://d.la4-c2-ia5.salesforceliveagent.com/chat',
        eswLiveAgentDevName: PRODUCT.eswLiveAgentDevName,
        isOfflineSupportEnabled: false,
      }
    );
  };

  if (!window.embedded_svc) {
    const s = document.createElement('script');
    s.setAttribute(
      'src',
      'https://plangrid.my.salesforce.com/embeddedservice/5.0/esw.min.js'
    );
    s.onload = function () {
      initESW(null);
    };
    document.body.appendChild(s);
  } else {
    initESW('https://service.force.com');
  }
}

function loadScripts(onLoad?: () => void, product?: ProductId): void {
  function addScript(script: Partial<HTMLScriptElement>, onload?: () => void) {
    const scriptElement = document.createElement('script');

    Object.assign(scriptElement, script);

    scriptElement.type = 'text/javascript';
    if (onload) scriptElement.onload = onload;
    const s = document.getElementsByTagName('script')[0];
    if (s) (s.parentNode as HTMLElement).insertBefore(scriptElement, s);
  }

  function addScripts(
    scripts: Array<Partial<HTMLScriptElement>>,
    onLastLoad?: () => void
  ) {
    const onload = scripts.slice(1).length
      ? addScripts.bind(null, scripts.slice(1), onLastLoad)
      : onLastLoad;
    addScript(scripts[0], onload);
  }

  const style = document.createElement('style');
  document.head.appendChild(style);
  style.appendChild(document.createTextNode(CSS_STYLES));

  (function () {
    // Load Solvvy first then additional chat scripts
    const scripts = [
      {
        async: true,
        src: 'https://cdn.solvvy.com/deflect/customization/autodesk_construction_cloud/solvvy.js',
      },
      {
        src: 'https://plangrid.my.salesforce.com/embeddedservice/5.0/esw.min.js',
      },
    ];

    addScripts(scripts, () => {
      initializeChat(product);
      onLoad && onLoad();
    });
  })();
}

const isScreener = () => window.location.hostname.includes('ngrok');

export default async function loadSupportScripts(
  onLoad?: () => void,
  product?: ProductId
) {
  if (window.SUPPORT_SCRIPTS_LOADED || isScreener()) return;

  loadScripts(onLoad, product);
  window.SUPPORT_SCRIPTS_LOADED = true;
}
